import React, { useState } from 'react'
import { Typography, Box, TextField } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import useAxios from '../../hooks/useAxios'
import {
  ApiStatus,
  ApiStatusMessage,
  formDesc,
  formHeading,
  LoadingStatus,
  submitBtnText,
} from '../../types/doNotSell'
import LoaderButton from '../Buttons/LoaderButton'
import GrayCheckmark from '../../images/checkmark-gray.inline.svg'
import GreenCheckmark from '../../images/checkmarkgreen.inline.svg'
import RedCross from '../../images/crossmark-red.inline.svg'
import usePageData from '../../hooks/usePageData'
import { BrandNames } from '../../types/brand'
import useLocation from 'react-use/lib/useLocation'

interface Props {
  sectionClass?: string
}

const DoNotSell: React.FC<Props> = ({ sectionClass }) => {
  const [email, setEmail] = useState('')
  const [apiStatus, setApiStatus] = useState('')
  const [loading, setLoading] = useState(LoadingStatus.Idle)
  const axios = useAxios()
  const { brand } = usePageData()
  const { classes, cx } = useStyles()
  const { hostname } = useLocation()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Reset the previous status when someone submits again
    setApiStatus('')

    setLoading(LoadingStatus.Loading)

    const data = new FormData()
    data.append('email', email)

    try {
      await axios.post(
        '/v2/msg/transactional/do-not-sell',
        {
          email_address: email,
          site_domain: hostname,
        },
        {
          baseURL: `https://${
            process.env.GATSBY_TALENTINC_API_HOST ?? 'api.talentinc.com'
          }`,
        }
      )

      setApiStatus(ApiStatus.Success)
    } catch (_) {
      setApiStatus(ApiStatus.Failure)
    } finally {
      setEmail('')
      setLoading(LoadingStatus.Idle)
    }
  }

  return (
    <Box className={cx(sectionClass, classes.formContainer)}>
      <Typography variant="h2" className={classes.formHeading}>
        {formHeading}
      </Typography>
      <Typography>{formDesc}</Typography>
      <form onSubmit={handleSubmit} className={classes.form}>
        <TextField
          className={classes.textField}
          type="email"
          InputProps={{
            classes: {
              notchedOutline: classes.notchedOutline,
            },
          }}
          InputLabelProps={{
            classes: {
              outlined: classes.label,
              shrink: 'shrink',
            },
          }}
          label="Your email"
          aria-label="Your email"
          variant="outlined"
          required
          value={email}
          onChange={handleChange}
        />
        <LoaderButton
          className={classes.submitButton}
          color="primary"
          variant="contained"
          type="submit"
          aria-label="submit"
          loading={loading === LoadingStatus.Loading}
          disabled={loading === LoadingStatus.Loading}
        >
          {submitBtnText}
        </LoaderButton>
      </form>
      {apiStatus === ApiStatus.Success && (
        <StatusMessage
          Icon={
            brand?.name.toLowerCase() === BrandNames.zipJob
              ? GreenCheckmark
              : GrayCheckmark
          }
          message={ApiStatusMessage.Success}
          className={classes.statusSuccess}
        />
      )}
      {apiStatus === ApiStatus.Failure && (
        <StatusMessage
          Icon={RedCross}
          message={ApiStatusMessage.Failure}
          className={classes.statusFailure}
        />
      )}
    </Box>
  )
}

interface StatusMessageProps {
  Icon: React.FC
  message: string
  className?: string
}

const StatusMessage: React.FC<StatusMessageProps> = ({
  Icon,
  message,
  className,
}) => {
  const { classes: defaultClasses, cx } = useStyles()

  return (
    <Box className={cx(defaultClasses.statusMessageContainer, className)}>
      <Icon />
      <Typography className={defaultClasses.message}>{message}</Typography>
    </Box>
  )
}

const useStyles = makeStyles()((theme) => ({
  formContainer: {
    margin: '4rem auto 0',
    textAlign: 'center',
    backgroundColor: theme.colors.dns.background,
    color: theme.colors.dns.text,
    padding: '3% 18% 6%',
    borderRadius: '6px',

    [theme.breakpoints.down('md')]: {
      padding: '2% 10% 4%',
      marginTop: '2rem',
    },
  },
  form: {
    marginTop: '1rem',
    display: 'flex',
    justifyContent: 'center',

    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      marginTop: '0.5rem',
      gap: '0.5rem',
    },
  },
  formHeading: {
    margin: '0.625rem 0',
  },
  textField: {
    flexBasis: '70%',
    backgroundColor: theme.palette.background.default,
    borderRadius: '6px 0 0 6px',

    [theme.breakpoints.down('md')]: {
      borderRadius: '6px',
    },
  },
  notchedOutline: {
    border: 'none',

    '&:focus': {
      border: '1px',
    },
  },
  label: {
    transform: 'translate(15%, -50%)',
    top: '50%',

    '&.shrink': {
      top: 0,

      [theme.breakpoints.down('md')]: {
        top: '-5%',
      },
    },
  },
  submitButton: {
    borderRadius: '0 6px 6px 0',
    flexBasis: '30%',

    '&:focus': {
      outline: 'none',
    },

    [theme.breakpoints.down('md')]: {
      borderRadius: '6px',
    },
  },
  statusMessageContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '1rem',
    padding: '0.625rem 1.25rem',
    gap: '0.75rem',
    borderRadius: '6px',

    '& svg': {
      flexShrink: 0,
    },

    [theme.breakpoints.down('md')]: {
      gap: '0.25rem',
      padding: '0.312rem 0.625rem',
      marginTop: '0.5rem',
    },
  },
  statusSuccess: {
    backgroundColor: theme.colors.dns.statusBackgroundSuccess,
    color: theme.colors.dns.statusMessageSuccess,
  },
  statusFailure: {
    backgroundColor: theme.colors.dns.statusBackgroundFailure,
    color: theme.colors.dns.statusMessageFailure,
  },
  message: {
    textAlign: 'left',
  },
}))

export default DoNotSell
